// import { globalConnectionManager } from '@/api/sse/prepare-chat'
import SheetColumnContextMenu from '@/components/ColumnContextMenu/SheetColumnContextMenu'
import { EnrichmentProgressOverlay } from '@/components/sheet/EnrichmentProgressOverlay'
import { SheetOverlay } from '@/components/sheet/sheet-overlay'
import { FieldsContext } from '@/contexts/FieldsContext'
import { useSheet } from '@/contexts/SheetContext'
import { useSheetTheme } from '@/contexts/SheetThemeContext'
import { useSheetCounts } from '@/hooks/sheet/useSheetCounts'
import { useSheetRecords } from '@/hooks/sheet/useSheetRecords'
import { useSheetViewData } from '@/hooks/sheet/useSheetViewData'

import { CellContextMenu } from '@/components/CellContextMenu/CellContextMenu'
import { MentionsWrapper } from '@/components/Mentions/MentionsWrapper'
import { useFilterSheet } from '@/components/sheet/store/hooks/useFilterSheet'
import {
  ColumnContextMenuSlotProps,
  DataBufferProvider,
  Table,
} from '@flatfile/turntable'
import { useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useContext, useMemo, useState } from 'react'
import { SheetTabs } from './SheetTabs'

interface SheetContentProps {
  readonly sourceSheetId: string
  readonly fileId: string
  setShowEnrichment: (showEnrichment: boolean) => void
  showEnrichment: boolean
}

function SheetContent({
  sourceSheetId,
  fileId,
  setShowEnrichment,
  showEnrichment,
}: SheetContentProps) {
  const [_tableIsEditing, setTableIsEditing] = useState(false)
  const { density } = useSheetTheme()
  const { sheet } = useSheet()
  const {
    columnConfigWithState: columnConfig,
    extraKeys,
    lockNumber,
  } = useContext(FieldsContext)

  const { fetchRecords, addOrUpdateRecords, hydrateAllRecords, getRecordById } =
    useSheetRecords()

  const { isLoading } = useQuery({
    queryKey: ['allRecords', sourceSheetId],
    queryFn: () => hydrateAllRecords(),
    staleTime: Infinity,
    enabled: !!sheet?.id,
  })

  return (
    <div className='flex flex-col h-full'>
      {/* Sheet grid using Turntable */}
      <div className='flex-1 min-h-0 relative'>
        <SheetOverlay isVisible={isLoading} />
        <AnimatePresence>
          {!isLoading && (
            <DataBufferProvider
              onLoadRows={fetchRecords}
              onUpdateRows={addOrUpdateRecords}
            >
              <motion.div
                className='absolute inset-0'
                initial={{ opacity: 0, filter: 'blur(8px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(8px)' }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                }}
              >
                <SheetViewInner
                  key={density}
                  columnConfig={columnConfig}
                  setTableIsEditing={setTableIsEditing}
                  sourceSheetId={sourceSheetId}
                  extraKeys={extraKeys}
                  lockNumber={lockNumber}
                  fileId={fileId}
                  getRecordById={getRecordById}
                  setShowEnrichment={setShowEnrichment}
                  showEnrichment={showEnrichment}
                />
              </motion.div>
            </DataBufferProvider>
          )}
        </AnimatePresence>
        <SheetTabs fileId={fileId} path={``} />
      </div>
    </div>
  )
}

export function SheetViewInner({
  columnConfig,
  setTableIsEditing,
  sourceSheetId,
  extraKeys,
  lockNumber,
  fileId,
  getRecordById,
  setShowEnrichment,
  showEnrichment,
}: {
  readonly columnConfig: any
  readonly extraKeys: string[]
  readonly setTableIsEditing: (isEditing: boolean) => void
  readonly sourceSheetId: string
  readonly fileId: string
  lockNumber: number
  getRecordById: (params: {
    sheetId: string
    recordId: string
  }) => Promise<{ dataRowIndex: number; record: any }>
  setShowEnrichment: (showEnrichment: boolean) => void
  showEnrichment: boolean
}) {
  const { sheetCounts } = useSheetCounts({ sheetId: sourceSheetId })
  const { filters } = useFilterSheet(sourceSheetId)
  const { theme } = useSheetTheme()

  useSheetViewData({
    sheetId: sourceSheetId,
    sheet: {},
    workbookId: sourceSheetId,
    columnConfig: columnConfig,
    searchFields: filters,
    rowCount: sheetCounts?.counts?.total ?? 0,
    lockNumber,
    getRecordById: getRecordById,
  })

  const usedColumnConfig = useMemo(() => {
    return [
      ...columnConfig,
      ...extraKeys.map((key) => ({
        value: key,
        label: key,
        type: 'text',
        treatments: ['ai-defined'],
      })),
    ]
  }, [columnConfig, extraKeys])

  const handleFindAndReplace = () => {
    console.log('handleFindAndReplace')
  }

  const getJobQuery = () => {
    console.log('getJobQuery')
    return {}
  }

  const handleCreateQueryByValue = () => {
    console.log('handleCreateQueryByValue')
  }

  const handleCreateQueryBySearchValue = () => {
    console.log('handleCreateQueryBySearchValue')
  }

  // State to track which input is open (by column and type)
  const [openInput, setOpenInput] = useState<{
    columnId: string
    inputType: string
  } | null>(null)

  const BoundSheetColumnContextMenu = useMemo(() => {
    const Component = (props: ColumnContextMenuSlotProps) => (
      <MentionsWrapper>
        <SheetColumnContextMenu
          handleFindAndReplace={handleFindAndReplace}
          getJobQuery={getJobQuery}
          onQueryByValue={handleCreateQueryByValue}
          onQueryBySearchValue={handleCreateQueryBySearchValue}
          isLastColumn={false}
          openInput={openInput}
          setOpenInput={setOpenInput}
          setShowEnrichment={setShowEnrichment}
          showEnrichment={showEnrichment}
          {...props}
        />
      </MentionsWrapper>
    )
    Component.displayName = 'BoundSheetColumnContextMenu'
    return Component
  }, [handleFindAndReplace, getJobQuery, openInput, setOpenInput])

  return (
    <>
      <Table
        readOnly={false}
        tableId='sheet-table'
        columnConfig={usedColumnConfig}
        totalRowCount={sheetCounts?.counts?.total ?? 0}
        setTableIsEditing={setTableIsEditing}
        pinningEnabled={true}
        showColumnMenus={true}
        columnSelectionEnabled={true}
        columnContextMenuSlot={BoundSheetColumnContextMenu}
        cellContextMenuSlot={CellContextMenu}
        theme={theme}
        additionalRows={50}
      />
      <EnrichmentProgressOverlay sheetId={sourceSheetId} />
    </>
  )
}

export function SheetView({
  sourceSheetId,
  fileId,
  showEnrichment,
  setShowEnrichment,
}: {
  readonly sourceSheetId: string
  readonly fileId: string
  showEnrichment: boolean
  setShowEnrichment: (showEnrichment: boolean) => void
}): any {
  const { isLoading, error, sheet } = useSheet()

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        Loading sheet blueprint...
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-full text-red-500'>
        Error loading sheet blueprint
      </div>
    )
  }

  if (!sheet) {
    return (
      <div className='flex items-center justify-center h-full'>
        No blueprint data available
      </div>
    )
  }

  return (
    <SheetContent
      sourceSheetId={sourceSheetId}
      fileId={fileId}
      setShowEnrichment={setShowEnrichment}
      showEnrichment={showEnrichment}
    />
  )
}
